import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Inbox, CheckCircle2, XCircle, RefreshCw, Receipt, User as UserIcon, FileText } from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import { formatDateTime } from '../lib/datetime';
import { ReceiptModal } from '../components/ReceiptModal';
import { RecordPaymentCard } from '../components/RecordPaymentCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Management-only page: dedicated view for reviewing resident-submitted
 * payments (bank transfers) awaiting approval. Extracted from Invoices.js
 * so it's a first-class destination — has its own sidebar entry, its own
 * auto-refresh loop, and a full-screen slip previewer.
 *
 * Approve/reject actions call the same `/api/admin/payments/{id}/{action}`
 * endpoints used previously.
 */
export const PendingApprovals = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'management';

  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [slipViewerUrl, setSlipViewerUrl] = useState(null);
  const [receiptSubmissionId, setReceiptSubmissionId] = useState(null);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setRefreshing(true);
    try {
      const [pRes, aRes] = await Promise.all([
        axios.get(`${API}/admin/payments/pending`, { withCredentials: true }),
        axios.get(`${API}/admin/payments/history?limit=50`, { withCredentials: true }),
      ]);
      setPending(pRes.data);
      // Filter to approved only — the history endpoint returns both approved
      // and rejected; rejected have no receipt so exclude them here.
      setApproved((aRes.data || []).filter((s) => s.status === 'approved'));
    } catch (err) {
      console.error('PendingApprovals: failed to load', err);
      toast.error(err.response?.data?.detail || 'Failed to load pending approvals');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    load();
    // Poll every 30s so approvals surfacing from another admin's session
    // (or a new resident submission) show up without a manual refresh.
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  const reviewSubmission = async (submissionId, action) => {
    let body = {};
    if (action === 'reject') {
      const reason = window.prompt('Reject reason (shown to the resident):');
      if (!reason || !reason.trim()) return;
      body = { reason: reason.trim() };
    }
    try {
      await axios.post(`${API}/admin/payments/${submissionId}/${action}`, body, { withCredentials: true });
      toast.success(action === 'approve' ? 'Approved. Invoice balances updated.' : 'Rejected.');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${action}`);
    }
  };

  const openSlip = (slipPath) => {
    if (!slipPath) return;
    const token = document.cookie.match(/access_token=([^;]+)/)?.[1] || '';
    setSlipViewerUrl(`${API}/files/${slipPath}?auth=${encodeURIComponent(token)}`);
  };

  if (!isAdmin) {
    return (
      <Card className="p-8 text-center bg-white border border-[#e2e8f0]" data-testid="pending-approvals-unauthorized">
        <p className="text-[#64748b]">This page is available to management only.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="pending-approvals-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Outfit' }}>
            Payments
          </h1>
          <p className="text-sm text-[#64748b]">
            Review resident bank-transfer submissions, approve or reject them, and issue receipts for
            approved payments. Approving one applies the amount to the oldest unpaid invoice first
            (or the specific invoice if targeted) and banks any surplus as a credit balance.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="border-[#e2e8f0]"
            onClick={load}
            disabled={refreshing}
            data-testid="refresh-approvals-button"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
          <Link
            to="/invoices"
            className="text-sm text-[#2563eb] hover:underline inline-flex items-center gap-1"
            data-testid="go-to-invoices-link"
          >
            <Receipt className="h-4 w-4" /> Back to Invoices
          </Link>
        </div>
      </div>

      {/* Record a manual payment (cash / bank-deposit / cheque). Auto-opens
          the receipt modal on success so admin can print / email immediately. */}
      {isAdmin && (
        <RecordPaymentCard
          onRecorded={(subId) => {
            setReceiptSubmissionId(subId);
            load();
          }}
        />
      )}

      {loading ? (
        <Card className="p-12 text-center bg-white border border-[#e2e8f0]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563eb] mx-auto mb-3" />
          <p className="text-sm text-[#64748b]">Loading pending approvals…</p>
        </Card>
      ) : pending.length === 0 ? (
        <Card className="p-12 text-center bg-white border border-[#e2e8f0]" data-testid="approvals-empty-state">
          <Inbox className="h-12 w-12 text-[#cbd5e1] mx-auto mb-4" />
          <p className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>All clear</p>
          <p className="text-sm text-[#64748b] mt-1">
            No pending payment approvals right now. Refreshes automatically every 30 seconds.
          </p>
        </Card>
      ) : (
        <Card className="p-6 bg-amber-50 border border-amber-200" data-testid="approvals-list">
          <p className="text-sm text-amber-900 mb-4">
            <strong>{pending.length}</strong> submission{pending.length === 1 ? '' : 's'} awaiting your review.
          </p>
          <div className="space-y-3">
            {pending.map(s => (
              <div
                key={s._id}
                className="flex flex-wrap items-start justify-between gap-4 p-4 bg-white rounded border border-amber-200"
                data-testid={`pending-${s._id}`}
              >
                <div className="flex-1 min-w-[260px]">
                  <p className="text-sm">
                    <strong className="text-[#0f172a]">
                      LKR {Number(s.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </strong>
                    {' · '}
                    <span className="capitalize">{(s.method || '').replace('_', ' ')}</span>
                    {' · '}
                    <span className="text-[#64748b]">
                      {s.invoice_id ? 'Targeted (one invoice)' : 'Apply to oldest first'}
                    </span>
                  </p>
                  <p className="text-xs text-[#64748b] mt-1">
                    <UserIcon className="inline h-3 w-3 mr-1" />
                    {s.submitted_by_name || 'Resident'}
                    {s.property_name ? ` · ${s.property_name}` : ''}
                    {' · submitted '}{formatDateTime(s.submitted_at)}
                  </p>
                  {s.note && (
                    <p className="text-xs text-[#334155] mt-1">
                      Note: <em>"{s.note}"</em>
                    </p>
                  )}
                  {s.slip_path && (
                    <button
                      type="button"
                      onClick={() => openSlip(s.slip_path)}
                      className="text-xs text-blue-600 hover:underline mt-2 inline-flex items-center gap-1"
                      data-testid={`view-slip-${s._id}`}
                    >
                      <FileText className="h-3 w-3" /> View slip
                    </button>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    onClick={() => reviewSubmission(s._id, 'approve')}
                    className="bg-[#10b981] hover:bg-[#059669] text-white"
                    data-testid={`approve-${s._id}`}
                  >
                    <CheckCircle2 className="mr-1 h-4 w-4" /> Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => reviewSubmission(s._id, 'reject')}
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    data-testid={`reject-${s._id}`}
                  >
                    <XCircle className="mr-1 h-4 w-4" /> Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Approved payment history — issue receipts anytime. */}
      {!loading && (
        <Card className="p-6 bg-white border border-[#e2e8f0]" data-testid="approved-payments-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Recent approved payments</h2>
              <p className="text-xs text-[#64748b]">Print or email a receipt for any approved payment.</p>
            </div>
            <span className="text-xs text-[#64748b]">{approved.length} shown</span>
          </div>
          {approved.length === 0 ? (
            <p className="text-sm text-[#94a3b8] italic">No approved payments yet.</p>
          ) : (
            <div className="divide-y divide-[#f1f5f9]">
              {approved.map((s) => (
                <div
                  key={s._id}
                  className="flex flex-wrap items-center justify-between gap-3 py-3"
                  data-testid={`approved-${s._id}`}
                >
                  <div className="flex-1 min-w-[240px]">
                    <p className="text-sm">
                      <strong className="text-[#0f172a]">
                        LKR {Number(s.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </strong>
                      {' · '}
                      <span className="capitalize text-[#334155]">{(s.method || '').replace('_', ' ')}</span>
                      {' · '}
                      <span className="text-[#64748b]">
                        {s.submitted_by_role === 'management' ? 'Recorded by ' : 'Submitted by '}
                        {s.submitted_by_name || 'Resident'}
                      </span>
                    </p>
                    <p className="text-xs text-[#64748b] mt-1">
                      Approved {formatDateTime(s.reviewed_at)}
                      {s.receipt_number ? ` · Receipt ${s.receipt_number}` : ''}
                      {s.receipt_emailed_at ? ` · Emailed ${formatDateTime(s.receipt_emailed_at)}` : ''}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setReceiptSubmissionId(s._id)}
                    className="text-[#2563eb] border-[#2563eb]/40 hover:bg-[#2563eb]/5"
                    data-testid={`open-receipt-${s._id}`}
                  >
                    <Receipt className="mr-1 h-4 w-4" />
                    Receipt
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Full-screen slip previewer */}
      {slipViewerUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setSlipViewerUrl(null)}
          data-testid="slip-viewer-overlay"
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-3 border-b border-[#e2e8f0]">
              <p className="text-sm font-semibold" style={{ fontFamily: 'Outfit' }}>Payment slip</p>
              <button
                onClick={() => setSlipViewerUrl(null)}
                className="text-[#64748b] hover:text-[#0f172a] text-sm"
                data-testid="close-slip-viewer"
              >
                Close
              </button>
            </div>
            <div className="flex-1 overflow-auto bg-[#f8fafc] flex items-center justify-center p-4">
              {slipViewerUrl.match(/\.pdf(\?|$)/i) ? (
                <iframe
                  title="Payment slip"
                  src={slipViewerUrl}
                  className="w-full h-[75vh] bg-white"
                  data-testid="slip-iframe"
                />
              ) : (
                <img
                  src={slipViewerUrl}
                  alt="Payment slip"
                  className="max-w-full max-h-[75vh] object-contain"
                  data-testid="slip-image"
                />
              )}
            </div>
          </div>
        </div>
      )}
      {receiptSubmissionId && (
        <ReceiptModal
          submissionId={receiptSubmissionId}
          onClose={() => { setReceiptSubmissionId(null); load(); }}
        />
      )}
    </div>
  );
};

export default PendingApprovals;
